import { requestPrivateFees } from "../utils/relayer-client";
import { usesPrivateFees } from "./privateFees";
import { privatePools } from "./privatePools";
import { EVM_CONFIG } from "../config";
import {
  loadEncryptedJsonCache,
  saveEncryptedJsonCache,
} from "../base-app/helpers/storage";

type RecoveryCache = { version: 1; epoch: string; after: number; rows: any[] };
type RecoveryOptions = {
  address: string;
  cacheSecret: string;
  recipientKey: string;
  signal?: AbortSignal;
};

/** Ciphertexts are only candidates. Normal wallet sync verifies commitments,
 * token domains and nullifiers before adding anything to a spendable balance. */
export async function fetchPrivateFeeRecovery(options?: RecoveryOptions) {
  if (!usesPrivateFees()) return [];
  const pages = await Promise.all(
    privatePools().map(async (pool) => {
      const scope = `stealthos-fee-recovery-v1:${EVM_CONFIG.CHAIN_ID}:${pool.toLowerCase()}:${options?.recipientKey ?? ""}`;
      const storageKey = `${scope}:${options?.address.toLowerCase() ?? ""}`;
      let rows: any[] = [];
      let after = 0;
      let epoch: string | undefined;
      let resets = 0;
      if (options?.cacheSecret) {
        try {
          const cache = await loadEncryptedJsonCache<RecoveryCache>(
            scope,
            storageKey,
            options.address,
            options.cacheSecret,
          );
          if (
            cache?.version === 1 &&
            /^[a-f0-9]{32}$/.test(cache.epoch) &&
            Number.isSafeInteger(cache.after) &&
            cache.after >= 0 &&
            Array.isArray(cache.rows) &&
            cache.rows.every(
              (row, i) =>
                Number.isSafeInteger(row.position) &&
                row.position > (i ? cache.rows[i - 1].position : 0),
            ) &&
            (cache.rows[cache.rows.length - 1]?.position ?? 0) === cache.after
          ) {
            rows = cache.rows;
            after = cache.after;
            epoch = cache.epoch;
          }
        } catch {
          /* Missing/corrupt cache restarts complete owner recovery. */
        }
      }
      for (;;) {
        const page = await requestPrivateFees<{
          notes: any[];
          epoch?: string;
          reset?: boolean;
        }>("recover", { after, epoch }, { pool, signal: options?.signal });
        if (
          !Array.isArray(page.notes) ||
          page.notes.length > 100 ||
          (page.epoch !== undefined && !/^[a-f0-9]{32}$/.test(page.epoch))
        )
          throw Error("Recovery response is unavailable");
        if (epoch && page.epoch !== epoch) {
          if (++resets > 1)
            throw Error(
              "Recovery service changed during refresh. Please try again.",
            );
          rows = [];
          after = 0;
          epoch = undefined;
          // Old servers do not understand cursor generations. Restart at zero
          // rather than silently skipping recovery during a rollback.
          if (page.reset !== true) continue;
        }
        epoch = page.epoch;
        for (const row of page.notes) {
          if (!Number.isSafeInteger(row.position) || row.position <= after)
            throw Error("Recovery cursor changed");
          rows.push(row);
          after = row.position;
        }
        if (page.notes.length < 100) break;
      }
      if (options?.signal?.aborted)
        throw new DOMException("Cancelled", "AbortError");
      // Ciphertexts and cursor are one authenticated record. A crash before the
      // wallet saves decrypted notes cannot make the cursor skip those notes.
      if (options?.cacheSecret && epoch && /^[a-f0-9]{32}$/.test(epoch)) {
        try {
          await saveEncryptedJsonCache(
            scope,
            storageKey,
            options.address,
            { version: 1, epoch, after, rows } satisfies RecoveryCache,
            options.cacheSecret,
          );
        } catch {
          /* Retry from the prior durable cursor next time. */
        }
      }
      return rows.map((row) => ({
        ...row.envelope,
        commitment: row.commitment,
      }));
    }),
  );
  return pages.flat();
}
